import {
  mapSurveyLocalization,
  QUOTA_CATEGORY,
  type Quota,
  type QuotaCriteria,
  type SurveyMetadataForm,
  type SurveyQualification,
  type SurveyRangeSet,
} from '@ps/survey-utils';
import { BasePayloadMapper } from '@ps/utils';

import type {
  SimilarSurveysCriteriaDto,
  SimilarSurveysQualificationDto,
  SimilarSurveysQuotaDto,
  SimilarSurveysRequestDto,
} from '../dtos/similar-surveys.dto';

export interface SimilarSurveysQuery {
  readonly metadata: SurveyMetadataForm;
  readonly qualifications: readonly SurveyQualification[];
  readonly quotas: readonly Quota[];
}

export class SimilarSurveysPayloadMapper extends BasePayloadMapper<
  SimilarSurveysQuery,
  SimilarSurveysRequestDto
> {
  public override toPayload({
    metadata,
    qualifications,
    quotas,
  }: SimilarSurveysQuery): SimilarSurveysRequestDto {
    return {
      survey_localization: this._localization(metadata),
      completes_required: metadata.completes ?? 0,
      expected_ir: metadata.incidence ?? 0,
      expected_loi: metadata.loi ?? 0,
      field_time: metadata.fieldTime ?? 0,
      qualifications: qualifications.map((qualification) =>
        this._qualification(qualification),
      ),
      quotas: quotas.map((quota) => this._quota(quota)),
    };
  }

  private _localization(metadata: SurveyMetadataForm): string {
    return mapSurveyLocalization(
      metadata.selectedCountries[0] ?? '',
      metadata.selectedLanguages[0] ?? '',
    );
  }

  private _qualification(
    qualification: SurveyQualification,
  ): SimilarSurveysQualificationDto {
    return {
      qualification_code: qualification.qualificationCode,
      ...(qualification.isRangeType
        ? toRangeCriteria(qualification.rangeSets)
        : toConditionCriteria(qualification.conditionCodes)),
    };
  }

  private _quota(quota: Quota): SimilarSurveysQuotaDto {
    return {
      required_count: quota.requiredCount,
      quota_category: quota.quotaCategory ?? QUOTA_CATEGORY.LAYERED,
      criteria: quota.criteria.map((criterion) =>
        this._quotaCriterion(criterion),
      ),
    };
  }

  private _quotaCriterion(
    criterion: QuotaCriteria,
  ): SimilarSurveysQualificationDto {
    return {
      qualification_code: criterion.qualificationCode,
      ...(criterion.rangeSets?.length
        ? toRangeCriteria(criterion.rangeSets)
        : toConditionCriteria(criterion.conditionCodes)),
    };
  }
}

function toRangeCriteria(
  rangeSets: readonly SurveyRangeSet[],
): SimilarSurveysCriteriaDto {
  return { range_sets: rangeSets.map((range) => ({ ...range })) };
}

function toConditionCriteria(
  conditionCodes: readonly string[],
): SimilarSurveysCriteriaDto {
  return { condition_codes: [...conditionCodes] };
}
